import AddCircleIcon from '@mui/icons-material/AddCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PersonIcon from '@mui/icons-material/Person';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, ClickAwayListener, FormControl, MenuItem, Select, Typography, useTheme } from "@mui/material";
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { createGlobalStyle } from "styled-components";
import { useTranslation } from '../utility/function/useTranslations';
import styles from './addRoom.module.css';

interface RoomObjType {
    adultCount: number
    childCount: number
    childAges: (number | null)[]
}

export default function NewMobileAddRoom(props: any) {
    const { widgetValues, setFieldValue, maxChildAge, guestObj, openGuest, setOpenGuest, fontColor, booknowbuttoncolor, booknowbuttonhovercolor, setIsAddRoomExpanded, brandId, locale } = props
    const theme = useTheme();
    const defaultAdults = brandId === 'cfd8297c-a349-4f17-999b-8f5837abed81' ? 1 : 2;
    const [isExpanded, setIsExpanded] = useState(false);
    const [roomObj, setRoomObj] = useState<[any]>(_.cloneDeep(guestObj));
    const [adultCount, setAdult] = useState<number>(2);
    const [childCount, setChild] = useState<number>(0);
    const { t } = useTranslation();
    const handleToggleExpand = () => {
        setIsExpanded(!isExpanded);
        setOpenGuest(!isExpanded);
    };

    useEffect(() => {
        setIsExpanded(openGuest)
        setIsAddRoomExpanded(openGuest);
    }, [openGuest])

    // Initialize childAges when guestObj changes
    useEffect(() => {
        const initializedGuestObj = guestObj.map((room: any) => ({
            ...room,
            childAges: room.childAges || Array(room.childCount || 0).fill(null)
        }));
        if (JSON.stringify(initializedGuestObj) !== JSON.stringify(roomObj)) {
            setRoomObj(initializedGuestObj);
        }
    }, [guestObj])

    const revertValues = () => {
        const revertedRooms = _.cloneDeep(widgetValues.guest).map((room: any) => ({
            ...room,
            childAges: room.childAges || Array(room.childCount || 0).fill(null)
        }));
        setRoomObj(revertedRooms);
    }

    const removeRoom = () => {
        const newRoomObj: [RoomObjType] = [...roomObj];
        if (newRoomObj.length > 1) {
            newRoomObj.pop()
        }
        setRoomObj(newRoomObj);
    }

    const changeCounter = (isIncrement: boolean, index: number, type: string) => {
        const newRoomObj: any = [...roomObj];
        if (isIncrement) {
            newRoomObj[index][type] = newRoomObj[index][type] + 1;
            // When adding a child, add a new age entry (default to 0 - first age)
            if (type === 'childCount') {
                if (!newRoomObj[index].childAges) {
                    newRoomObj[index].childAges = [];
                }
                newRoomObj[index].childAges.push(0);
            }
        } else {
            newRoomObj[index][type] = newRoomObj[index][type] - 1;
            // When removing a child, remove the last age entry
            if (type === 'childCount' && newRoomObj[index].childAges && newRoomObj[index].childAges.length > 0) {
                newRoomObj[index].childAges.pop();
            }
        }

        setRoomObj(newRoomObj);
    }

    const AddRoom = () => {
        const newRoomObj: [RoomObjType] = [...roomObj];
        newRoomObj.push({ adultCount: defaultAdults, childCount: 0, childAges: [] });
        setRoomObj(newRoomObj)
    }

    const handleChildAgeChange = (roomIndex: number, childIndex: number, age: number) => {
        const newRoomObj: any = roomObj.map((room: any, idx: number) => {
            if (idx === roomIndex) {
                const updatedChildAges = [...(room.childAges || [])];
                // Ensure the array is long enough
                while (updatedChildAges.length <= childIndex) {
                    updatedChildAges.push(null);
                }
                updatedChildAges[childIndex] = age;
                return {
                    ...room,
                    childAges: updatedChildAges
                };
            }
            return room;
        });
        setRoomObj(newRoomObj);
    }

    useEffect(() => {
        setAdult(roomObj.reduce((n, { adultCount }) => n + adultCount, 0));
        setChild(roomObj.reduce((n, { childCount }) => n + childCount, 0));
    }, [roomObj, adultCount, childCount])

    const updateData = () => {
        const widgetDataCopy: any = [];
        roomObj.map((data: any, index: number): any => {
            const widgetDataObj = {
                roomCount: index + 1,
                adultCount: data?.adultCount,
                childCount: data?.childCount,
                childAges: data?.childAges || []
            }
            return widgetDataCopy.push(widgetDataObj);
        });

        setFieldValue('guest', widgetDataCopy, true);
        setIsExpanded(false);
        setOpenGuest(false);
    }

    const getTotal = (inputArray: any[], key: any) => inputArray.reduce(function (acc: any, obj: any) { return acc + obj[key]; }, 0);

    const handleCloseFilter = () => {
        revertValues();
        setIsExpanded(false);
        setOpenGuest(false);
    };

    const GlobalStyle = createGlobalStyle`
        .mobileAddRoomDropdown {
            position: absolute;
            z-index: 9;
            width: 86%;
            margin-left: -16px;
            border-radius: 4pt;
            border: 1px solid #E4E4E4;
            box-shadow: 1px 1px 1px #00000029;
            margin-top: 10px;
            background-color: #17202B;
            max-height: 400px;
            overflow-y: auto;
        }

        .roomDropdownContent {
            background-color: rgb(255, 255, 255);
        }

        .roomCard {
            background-color: ${booknowbuttoncolor ? booknowbuttoncolor : '#526781'};
            margin: 10px;
            border-radius: 8px;
            padding: 15px;
            border: 1px solid rgba(255,255,255,0.1);
            min-width: 80%;
        }

        .roomHeader {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 10px;
            border-bottom: 1px solid rgba(255,255,255,0.2);
        }

        .counterRow {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;
        }

        .counterLabel {
            display: flex;
            flex-direction: column;
            color: #fff;
            font-size: 14px;
            font-weight: 500;
            flex: 1;
        }

        .counterValue {
            color: #fff;
            font-size: 16px;
            font-weight: bold;
            min-width: 30px;
            text-align: center;
        }

        .counterButton {
            color: #fff;
            cursor: pointer;
            padding: 5px;
        }

        .counterButtonDisabled {
            color: rgba(255,255,255,0.3);
            cursor: not-allowed;
            padding: 5px;
        }

        .removeButton {
            border: none;
            border-radius: 6px;
            padding: 8px;
            font-size: 12px;
            cursor: pointer;
            margin-top: -8px;
        }

        .addRoomButton {
            width: 40px;
            height: 40px;
            background-color: ${booknowbuttoncolor ? booknowbuttoncolor : '#2753EB'};
            color: #fff;
            border: none;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            margin: 10px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.08);
        }

        .doneButton {
            width: calc(40% - 20px);
            background-color: ${booknowbuttoncolor ? booknowbuttoncolor : '#2952DD'};
            color: #fff;
            border: none;
            border-radius: 8px;
            padding: 10px;
            margin: 10px;
            font-size: 16px;
            font-weight: bold;
            cursor: pointer;
            text-transform: uppercase;
            margin-left: auto;
        }

        .roomButtonContainer {
            display: flex;
            justify-content: space-between;
        }

        .roomScrollContainer {
            display: flex;
            overflow-x: auto;
        }

        .childAgeRow {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            padding: 10px 0;
            border-top: 1px solid rgba(255,255,255,0.1);
            margin-top: 10px;
        }

        .childAgeLabel {
            color: #fff;
            font-size: 14px;
            font-weight: 500;
            text-transform: uppercase;
            flex: 1;
            min-width: 120px;
        }

        .childAgeSelect {
            min-width: 120px;
            background-color: rgba(255,255,255,0.95);
            border-radius: 6px;
            color: #000000;
            font-size: 14px;
            & .MuiOutlinedInput-notchedOutline {
                border-color: rgba(255,255,255,0.3);
            }
            &:hover .MuiOutlinedInput-notchedOutline {
                border-color: rgba(255,255,255,0.5);
            }
            &.Mui-focused .MuiOutlinedInput-notchedOutline {
                border-color: rgba(255,255,255,0.7);
            }
            & .MuiSelect-select {
                padding: 10px 14px;
                text-transform: lowercase;
                color: #000000;
            }
        }
    `;

    return (
        <ClickAwayListener onClickAway={handleCloseFilter}>
            <form style={{ marginBottom: '0px' }} onSubmit={(e) => e.preventDefault()}>
                <GlobalStyle />
                <Box component="div" className={styles.addRoomGuest}>
                    <p onClick={handleToggleExpand} style={{ color: fontColor ? fontColor : "#fff", fontSize: '14px', marginTop: '10px', fontWeight: '300', textTransform: 'uppercase', display: 'flex', marginBottom: '0px', cursor: 'pointer', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{guestObj.length} {t("common", roomObj.length > 1 ? "rooms" : "room", locale)}, {getTotal(widgetValues.guest, 'adultCount')} {t("widget", getTotal(widgetValues.guest, 'adultCount') > 1 ? 'guests' : 'guest', locale)}</span>
                        <PersonIcon sx={{ color: fontColor ? fontColor : "#fff", float: 'right', mr: '20px', fontSize: '24px', opacity: '100%' }} />
                    </p>

                    <Box component="div" className='mobileAddRoomDropdown' sx={{ display: isExpanded ? 'block' : 'none' }}>
                        <Box component="div" className='roomDropdownContent'>
                            <div className='roomScrollContainer'>
                                {roomObj.map((data: any, index: number) => (
                                    <Box key={`room-${index}`} component="div" className='roomCard'>
                                        <Box component="div" className='roomHeader'>
                                            <Typography variant="h6" style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                                                {t("common", "room", locale)} {index + 1}
                                            </Typography>
                                            {index > 0 && (
                                                <button type="button" onClick={removeRoom} className='removeButton'>
                                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                                        <svg width="16" height="16" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                                            <line x1="4" y1="4" x2="12" y2="12" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                                                            <line x1="12" y1="4" x2="4" y2="12" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                                                        </svg>
                                                    </span>
                                                </button>
                                            )}
                                        </Box>

                                        {/* Adults Counter */}
                                        <Box component="div" className='counterRow'>
                                            <Box component="div" className='counterLabel'>
                                                <Typography style={{ color: '#fff', fontSize: '14px', fontWeight: 500, textTransform: 'uppercase' }}>
                                                    {t("common", "adults", locale)}
                                                </Typography>
                                                <Typography style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>
                                                    {t("widget", "ages", locale)} {maxChildAge + 1} {t("widget", "orAbove", locale)}
                                                </Typography>
                                            </Box>
                                            <Box component="div" className={data?.adultCount > 1 ? 'counterButton' : 'counterButtonDisabled'}>
                                                {data?.adultCount > 1 ? (
                                                    <RemoveCircleIcon sx={{ fontSize: '24px' }} onClick={() => changeCounter(false, index, 'adultCount')} />
                                                ) : (
                                                    <RemoveCircleOutlineIcon sx={{ fontSize: '24px' }} />
                                                )}
                                            </Box>
                                            <Box component="div" className='counterValue'>
                                                {data?.adultCount}
                                            </Box>
                                            <Box component="div" className={data?.adultCount < 25 ? 'counterButton' : 'counterButtonDisabled'}>
                                                {data?.adultCount < 25 ? (
                                                    <AddCircleIcon sx={{ fontSize: '24px' }} onClick={() => changeCounter(true, index, 'adultCount')} />
                                                ) : (
                                                    <AddCircleOutlineIcon sx={{ fontSize: '24px' }} />
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Children Counter */}
                                        <Box component="div" className='counterRow' style={{ padding: '0px', paddingTop: '10px' }}>
                                            <Box component="div" className='counterLabel'>
                                                <Typography style={{ color: '#fff', fontSize: '14px', fontWeight: 500, textTransform: 'uppercase' }}>
                                                    {t("widget", "children", locale)}
                                                </Typography>
                                                <Typography style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>
                                                    {t("widget", "ages", locale)} 0-{maxChildAge}
                                                </Typography>
                                            </Box>
                                            <Box component="div" className={data?.childCount > 0 ? 'counterButton' : 'counterButtonDisabled'}>
                                                {data?.childCount > 0 ? (
                                                    <RemoveCircleIcon
                                                        sx={{ fontSize: '24px' }}
                                                        onClick={() => changeCounter(false, index, 'childCount')}
                                                    />
                                                ) : (
                                                    <RemoveCircleOutlineIcon sx={{ fontSize: '24px' }} />
                                                )}
                                            </Box>
                                            <Box component="div" className='counterValue'>
                                                {data?.childCount}
                                            </Box>
                                            <Box component="div" className={data?.childCount < 24 ? 'counterButton' : 'counterButtonDisabled'}>
                                                {data?.childCount < 24 ? (
                                                    <AddCircleIcon
                                                        sx={{ fontSize: '24px' }}
                                                        onClick={() => changeCounter(true, index, 'childCount')}
                                                    />
                                                ) : (
                                                    <AddCircleOutlineIcon sx={{ fontSize: '24px' }} />
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Child Age Dropdowns */}
                                        {data?.childCount > 0 && (
                                            <Box component="div" style={{ marginTop: '10px' }}>
                                                {Array.from({ length: data.childCount }, (_, childIndex) => (
                                                    <Box key={`child-age-${index}-${childIndex}`} component="div" className='childAgeRow'>
                                                        <Typography className='childAgeLabel'>
                                                            {t("widget", "child", locale)} {childIndex + 1} Age
                                                        </Typography>
                                                        <FormControl sx={{ minWidth: '120px' }}>
                                                            <Select
                                                                id={`child-age-select-${index}-${childIndex}`}
                                                                value={data?.childAges?.[childIndex] != null ? Number(data.childAges[childIndex]) : ''}
                                                                onChange={(e) => {
                                                                    const selectedAge = e.target.value;
                                                                    if (selectedAge !== '') {
                                                                        handleChildAgeChange(index, childIndex, Number(selectedAge));
                                                                    }
                                                                }}
                                                                displayEmpty
                                                                className='childAgeSelect'
                                                                required
                                                                MenuProps={{
                                                                    disablePortal: true,
                                                                    style: {
                                                                        zIndex: 9999
                                                                    },
                                                                    PaperProps: {
                                                                        style: {
                                                                            maxHeight: 200,
                                                                            zIndex: 9999
                                                                        }
                                                                    },
                                                                    anchorOrigin: {
                                                                        vertical: 'bottom',
                                                                        horizontal: 'left',
                                                                    },
                                                                    transformOrigin: {
                                                                        vertical: 'top',
                                                                        horizontal: 'left',
                                                                    }
                                                                }}
                                                                renderValue={(selected) => {
                                                                    if (selected.toString() === '' || selected === null || selected === undefined) {
                                                                        return <span style={{ color: '#000000' }}>{t("widget", "ages", locale)}</span>;
                                                                    }
                                                                    const ageValue = typeof selected === 'number' ? selected : Number(selected);
                                                                    if (isNaN(ageValue)) {
                                                                        return <span style={{ color: '#000000' }}>{t("widget", "ages", locale)}</span>;
                                                                    }
                                                                    return <span style={{ color: '#000000' }}>{ageValue === 0 ? '<1 yr' : `${ageValue} ${ageValue === 1 ? 'yr' : 'yrs'}`}</span>;
                                                                }}
                                                            >
                                                                <MenuItem value="" disabled>
                                                                    <em>{t("widget", "ages", locale)}</em>
                                                                </MenuItem>
                                                                {Array.from({ length: maxChildAge + 1 }, (_, age) => {
                                                                    return (
                                                                        <MenuItem style={{ textTransform: 'lowercase' }} key={age} value={age}>
                                                                            {age === 0 ? '<1 yr' : `${age} ${age === 1 ? 'yr' : 'yrs'}`}
                                                                        </MenuItem>
                                                                    );
                                                                })}
                                                            </Select>
                                                        </FormControl>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </div>
                            <div className='roomButtonContainer'>
                                {/* Add Another Room Button */}
                                {roomObj.length < 5 && (
                                    <button type="button" onClick={AddRoom} className='addRoomButton'>
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <line x1="10" y1="4" x2="10" y2="16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                                            <line x1="4" y1="10" x2="16" y2="10" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                                        </svg>
                                    </button>
                                )}
                                {/* Done Button */}
                                <button type="button" onClick={updateData} className='doneButton'>
                                    {t("common", "done", locale)}
                                </button>
                            </div>
                        </Box>
                    </Box>
                </Box>
            </form>
        </ClickAwayListener>
    )
}